import { useEffect, useState } from "react";
import { OwnerNav } from "@/components/Layout";
import { api } from "@/lib/api";
import { Toaster } from "sonner";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function Stats() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await api.get("/owner/stats", { params: { period } });
      if (!cancelled) setData(res.data);
    })();
    return () => { cancelled = true; };
  }, [period]);

  const avg = data && data.confirmed ? Math.round(data.revenue / data.confirmed) : 0;
  const maxWd = data ? Math.max(1, ...data.by_weekday) : 1;
  const maxSvc = data && data.by_service.length ? Math.max(...data.by_service.map((s) => s.revenue)) : 1;

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
      <OwnerNav />
      <Toaster theme="dark" />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-2">Statistiques</div>
            <h1 className="font-serif text-5xl font-light">Performances.</h1>
          </div>
          <div className="flex border border-[#2C2A26]">
            <button onClick={() => setPeriod("month")} className={`px-5 py-2 text-xs uppercase tracking-[0.2em] ${period === "month" ? "bg-[#C5A059] text-[#0A0908]" : "text-[#A39E93]"}`} data-testid="period-month">Ce mois</button>
            <button onClick={() => setPeriod("all")} className={`px-5 py-2 text-xs uppercase tracking-[0.2em] ${period === "all" ? "bg-[#C5A059] text-[#0A0908]" : "text-[#A39E93]"}`} data-testid="period-all">Tout</button>
          </div>
        </div>

        {!data ? <p className="text-[#6B6760]">Chargement…</p> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="stats-cards">
              <Metric label="Chiffre d'affaires" value={`${data.revenue}€`} testId="metric-revenue" />
              <Metric label="RDV confirmés" value={data.confirmed} testId="metric-confirmed" />
              <Metric label="Panier moyen" value={`${avg}€`} testId="metric-avg" />
              <Metric label="Clients uniques" value={data.unique_clients} testId="metric-clients" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="font-serif text-2xl font-light mb-6">Revenu par prestation</h2>
                {data.by_service.length === 0 ? <p className="text-[#6B6760] text-sm">Aucune donnée.</p> : data.by_service.map((s) => (
                  <div key={s.name} className="mb-4" data-testid={`stat-service-${s.name}`}>
                    <div className="flex justify-between text-sm mb-1"><span>{s.name}</span><span className="text-[#A39E93]">{s.revenue}€ · {s.count}</span></div>
                    <div className="h-2 bg-[#141311]"><div className="h-2 bg-[#C5A059]" style={{ width: `${(s.revenue / maxSvc) * 100}%` }} /></div>
                  </div>
                ))}
              </div>

              <div>
                <h2 className="font-serif text-2xl font-light mb-6">Affluence par jour</h2>
                <div className="flex items-end gap-3 h-40">
                  {data.by_weekday.map((n, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`stat-weekday-${i}`}>
                      <div className="w-full bg-[#C5A059]" style={{ height: `${(n / maxWd) * 100}%`, minHeight: n > 0 ? 4 : 0 }} />
                      <div className="text-[10px] text-[#6B6760] mt-2">{WEEKDAYS[i]}</div>
                      <div className="text-xs text-[#A39E93]">{n}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-12">
              <h2 className="font-serif text-2xl font-light mb-6">Par coiffeur</h2>
              {data.by_staff.length === 0 ? <p className="text-[#6B6760] text-sm">Aucune donnée.</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.by_staff.map((s) => (
                    <div key={s.name} className="bg-[#141311] border border-[#2C2A26] p-5" data-testid={`stat-staff-${s.name}`}>
                      <div className="font-medium">{s.name}</div>
                      <div className="text-sm text-[#A39E93] mt-1">{s.count} RDV · {s.revenue}€</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div className="bg-[#141311] border border-[#2C2A26] p-6" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#A39E93] mb-3">{label}</div>
      <div className="font-serif text-4xl font-light text-[#F4F0EA]">{value}</div>
    </div>
  );
}
